import { type JSX } from 'react';

import { AppLink } from '../../discovery/router';
import { routes } from '../../routes';

import { type Cart, type CartLine, type Store } from './store-api';
import { money, nudgeOf, offersDelivery, termsOf } from './store-model';
import { Icon, PATHS } from './store-icons';

export type Mode = 'DELIVERY' | 'COLLECTION';

/** The frame marks what is not built with this, and the control does nothing. */
export function Soon(): JSX.Element {
  return <span className="jt-st-soon">Coming soon</span>;
}

export function FeesLine({
  store,
  centred = false,
}: {
  readonly store: Store;
  readonly centred?: boolean;
}): JSX.Element {
  const terms = termsOf(store);
  return (
    <div className={`jt-st-fees${centred ? ' jt-st-fees--centre' : ''}`}>
      <span className="jt-st-fees__line">
        {terms.minimum}
        {terms.delivery !== undefined ? (
          <>
            {' · '}
            <b>Fees:</b> {terms.delivery}
          </>
        ) : null}
      </span>
      <span className="jt-st-fees__note">Fees apply. Add to basket to see the total price.</span>
    </div>
  );
}

function Toggle({ label, icon }: { readonly label: string; readonly icon: string }): JSX.Element {
  return (
    <div className="jt-st-toggle" aria-disabled="true">
      <Icon d={icon} size={18} />
      <span className="jt-st-toggle__label">{label}</span>
      <Soon />
      <span aria-hidden="true" className="jt-st-toggle__track">
        <span />
      </span>
    </div>
  );
}

function Line({
  line,
  currency,
  busy,
  onQuantity,
}: {
  readonly line: CartLine;
  readonly currency: string;
  readonly busy: boolean;
  readonly onQuantity: (line: CartLine, quantity: number) => void;
}): JSX.Element {
  const one = line.quantity <= 1;
  return (
    <div className="jt-st-line">
      <div className="jt-st-line__row">
        <span
          className="jt-st-line__img"
          style={line.imageUrl !== undefined ? { backgroundImage: `url("${line.imageUrl}")` } : undefined}
        />
        <span className="jt-st-line__name">{line.title}</span>
        <span className="jt-st-line__total">
          {line.lineTotalMinor !== undefined ? money(line.lineTotalMinor, currency) : 'Price to confirm'}
        </span>
      </div>
      <div className="jt-st-line__qty">
        <button
          type="button"
          disabled={busy}
          aria-label={one ? `Remove ${line.title}` : `Remove one ${line.title}`}
          onClick={() => onQuantity(line, line.quantity - 1)}
        >
          {one ? (
            <Icon d={PATHS.trash} size={16} stroke="#17112b" />
          ) : (
            <Icon d={PATHS.minus} size={14} stroke="#17112b" width={2.8} />
          )}
        </button>
        <span aria-live="polite">{line.quantity}</span>
        {/* A line answered on a consultation is for the quantity the answers were given for. */}
        <button
          type="button"
          disabled={busy || line.quantity >= 99 || line.consultationId !== undefined}
          aria-label={`Add another ${line.title}`}
          onClick={() => onQuantity(line, line.quantity + 1)}
        >
          <Icon d={PATHS.plus} size={15} stroke="#17112b" width={2.8} />
        </button>
      </div>
    </div>
  );
}

/**
 * The basket rail — the frame's right-hand column, a drawer below 760px.
 *
 * Drawn from the platform's basket and nothing else: the lines, the subtotal
 * and the unpriced lines are the server's. A delivery fee is only known at
 * checkout, once there is an address (the platform quotes it then), so the
 * rail says so instead of printing the shop's "from" figure as a charge. The
 * frame's service and small-order fees are not charged here (D6) and are not
 * drawn; "Order for someone else", the swap preference and the two
 * recommendation rails have nothing behind them and say "Coming soon".
 * "Go to checkout" opens the Checkout page (stage 6) with the chosen mode.
 */
export function StoreBasket({
  store,
  cart,
  busy,
  error,
  mode,
  onMode,
  onQuantity,
  onClear,
  narrow,
  open,
  onClose,
}: {
  readonly store: Store;
  readonly cart: Cart | undefined;
  readonly busy: boolean;
  readonly error: string;
  readonly mode: Mode;
  readonly onMode: (mode: Mode) => void;
  readonly onQuantity: (line: CartLine, quantity: number) => void;
  readonly onClear: () => void;
  readonly narrow: boolean;
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const lines = cart?.lines ?? [];
  const currency = cart?.currency ?? 'GBP';
  const elsewhere = lines.length > 0 && cart?.storefrontId !== undefined && cart.storefrontId !== store.id;
  const has = lines.length > 0 && !elsewhere;
  const count = cart?.itemCount ?? 0;
  const nudge = has ? nudgeOf(cart?.subtotalMinor ?? 0, store.minimumOrderMinor, currency) : undefined;
  const unpriced = (cart?.unpricedLineIds.length ?? 0) > 0;
  const modes: readonly { id: Mode; label: string; sub: string | undefined; off: boolean }[] = [
    {
      id: 'DELIVERY',
      label: 'Delivery',
      sub: store.deliveryEtaLabel,
      off: !offersDelivery(store),
    },
    {
      id: 'COLLECTION',
      label: 'Collection',
      sub: store.collectionEtaLabel,
      off: !store.fulfilmentModes.includes('COLLECTION'),
    },
  ];
  const subtotal = money(cart?.subtotalMinor ?? 0, currency);

  return (
    <aside
      aria-label="Basket"
      className={`jt-st-bk${narrow ? ' jt-st-bk--drawer' : ''}${narrow && open ? ' jt-st-bk--open' : ''}`}
      {...(narrow && !open ? { 'aria-hidden': true } : {})}
    >
      <div className="jt-st-bk__top">
        <div className="jt-st-bk__title">
          <h2>Basket</h2>
          {has ? (
            <span className="jt-st-bk__count">
              · {count} {count === 1 ? 'item' : 'items'}
            </span>
          ) : null}
          {narrow ? (
            <button
              type="button"
              className="jt-st-round jt-st-round--md"
              aria-label="Close basket"
              onClick={onClose}
            >
              <Icon d={PATHS.close} size={16} stroke="#17112b" width={2.6} />
            </button>
          ) : null}
        </div>
        <div role="tablist" aria-label="Delivery or collection" className="jt-st-modes">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              aria-disabled={m.off}
              className={`jt-st-modes__tab${mode === m.id ? ' jt-st-modes__tab--on' : ''}${m.off ? ' jt-st-modes__tab--off' : ''}`}
              onClick={() => {
                if (!m.off) onMode(m.id);
              }}
            >
              <span>{m.label}</span>
              <small>{m.off ? 'Not offered' : (m.sub ?? '')}</small>
            </button>
          ))}
        </div>
      </div>
      <div className="jt-st-bk__scroll">
        <Toggle label="Order for someone else" icon={PATHS.person} />
        {error !== '' ? (
          <p className="jt-st-alert" role="alert">
            {error}
          </p>
        ) : null}
        {elsewhere ? (
          <div className="jt-st-bk__elsewhere">
            <span className="jt-st-bk__elsehead">Your basket is with another pharmacy</span>
            <span>
              A basket holds one pharmacy&rsquo;s items at a time. It has {count}{' '}
              {count === 1 ? 'item' : 'items'} ({lines.map((line) => line.title).join(', ')}). Empty it to
              shop at {store.name}.
            </span>
            <button type="button" className="jt-st-pill" disabled={busy} onClick={onClear}>
              Empty basket
            </button>
          </div>
        ) : null}
        {!has && !elsewhere ? (
          <>
            <div className="jt-st-bk__empty">
              <span className="jt-st-bk__emptyicon">
                <Icon d={PATHS.basket} size={26} />
              </span>
              <span className="jt-st-bk__emptyhead">Your basket is empty</span>
              <span className="jt-st-bk__emptycopy">
                Add medicines and health products, or book a service.
              </span>
            </div>
            <div className="jt-st-bk__terms">
              <FeesLine store={store} centred />
            </div>
          </>
        ) : null}
        {has ? (
          <>
            {nudge !== undefined ? (
              <div className="jt-st-nudge">
                <span>
                  Spend another <b>{nudge.short}</b> {nudge.why}
                </span>
                <span className="jt-st-nudge__bar">
                  <span style={{ width: `${String(nudge.percent)}%` }} />
                </span>
              </div>
            ) : null}
            <div className="jt-st-swap" aria-disabled="true">
              <span className="jt-st-swap__icon">
                <Icon d={PATHS.swap} size={18} stroke="#17112b" width={2.2} />
              </span>
              <span className="jt-st-swap__text">
                Swap out-of-stock items for the <b>same medicine</b>, another brand <Soon />
              </span>
              <span aria-hidden="true" className="jt-st-toggle__track">
                <span />
              </span>
            </div>
            <div className="jt-st-lines">
              {lines.map((line) => (
                <Line key={line.id} line={line} currency={currency} busy={busy} onQuantity={onQuantity} />
              ))}
            </div>
            {lines.some((line) => line.consultationId !== undefined) ? (
              <p className="jt-st-note">
                <Icon d={PATHS.shield} size={16} />
                <span>
                  A pharmacist will check your answers for pharmacy medicines before they&rsquo;re sent.
                </span>
              </p>
            ) : null}
            <section aria-label="Did you forget?" className="jt-st-rec">
              <span className="jt-st-rec__head">Did you forget?</span>
              <span className="jt-st-rec__sub">
                Popular at {store.name} · <Soon />
              </span>
            </section>
            <section aria-label="Have you seen" className="jt-st-rec jt-st-rec--seen">
              <span className="jt-st-rec__head">Have you seen…</span>
              <span className="jt-st-rec__sub">
                Picked for your basket · <Soon />
              </span>
            </section>
          </>
        ) : null}
      </div>
      {has ? (
        <div className="jt-st-bk__foot">
          <div className="jt-st-bk__row">
            <span>Subtotal</span>
            <b>{subtotal}</b>
          </div>
          <div className="jt-st-bk__row">
            <span>Delivery fee</span>
            <b>{mode === 'COLLECTION' ? 'Free' : 'At checkout'}</b>
          </div>
          {unpriced ? (
            <p className="jt-st-bk__unpriced">
              Some items have no price yet. The pharmacy confirms it at checkout.
            </p>
          ) : null}
          <div className="jt-st-bk__total">
            <span>Total</span>
            <span>{mode === 'COLLECTION' ? subtotal : `${subtotal} + delivery`}</span>
          </div>
          <AppLink
            to={`${routes.checkout.path}?store=${encodeURIComponent(store.slug)}&mode=${mode}`}
            className="jt-st-cta jt-st-cta--go"
          >
            Go to checkout
          </AppLink>
        </div>
      ) : null}
    </aside>
  );
}
